import React from "react";

// Lightweight inline markdown → React. Renders **bold**, *italic* / _italic_, `code`,
// ~~strike~~, [text](url) and optional [n] citation buttons. No raw asterisks left behind.
function renderInline(text, keyPrefix, opts = {}) {
  const { citations, onCite, activeCite, linkClassName } = opts;
  const nodes = [];
  let remaining = String(text);
  let idx = 0;
  const re = /(\*\*([^*]+)\*\*)|(__([^_]+)__)|(\*([^*\n]+)\*)|(_([^_\n]+)_)|(~~([^~]+)~~)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)]+)\))|(\[(\d+)\])/;
  while (remaining) {
    const m = remaining.match(re);
    if (!m) { nodes.push(remaining); break; }
    if (m.index > 0) nodes.push(remaining.slice(0, m.index));
    const key = `${keyPrefix}-${idx++}`;
    if (m[1]) nodes.push(<strong key={key} className="font-semibold">{m[2]}</strong>);
    else if (m[3]) nodes.push(<strong key={key} className="font-semibold">{m[4]}</strong>);
    else if (m[5]) nodes.push(<em key={key} className="italic">{m[6]}</em>);
    else if (m[7]) nodes.push(<em key={key} className="italic">{m[8]}</em>);
    else if (m[9]) nodes.push(<span key={key} className="line-through opacity-80">{m[10]}</span>);
    else if (m[11]) nodes.push(<code key={key} className="px-1 py-0.5 rounded bg-black/20 text-[0.9em] font-mono-legal">{m[12]}</code>);
    else if (m[13]) nodes.push(<a key={key} href={m[15]} target="_blank" rel="noreferrer" className={linkClassName || "underline hover:opacity-80"}>{m[14]}</a>);
    else if (m[16]) {
      const n = parseInt(m[17], 10);
      const cit = citations && citations.find((c) => c.n === n);
      if (cit && onCite) {
        nodes.push(
          <button key={key} onClick={() => onCite(cit)} data-testid={`inline-cite-${n}-${idx}`}
            className={`inline-flex items-center justify-center align-baseline mx-0.5 px-1.5 h-[18px] rounded text-[10px] font-mono-legal lift ${activeCite === cit.quote ? "bg-primary text-primary-foreground" : "bg-primary/15 text-primary hover:bg-primary/25"}`}>
            {n}
          </button>
        );
      } else {
        nodes.push(m[16]);
      }
    }
    remaining = remaining.slice(m.index + m[0].length);
  }
  return nodes;
}

export function MarkdownLite({ text, className = "", citations, onCite, activeCite, linkClassName }) {
  if (!text && text !== 0) return null;
  const opts = { citations, onCite, activeCite, linkClassName };
  const lines = String(text).replace(/\r/g, "").split("\n");
  const elements = [];
  let list = null;
  let para = [];
  const flushPara = (k) => {
    if (!para.length) return;
    const rows = para;
    para = [];
    elements.push(
      <p key={`p-${k}`} className="whitespace-pre-wrap">
        {rows.map((ln, i) => (
          <React.Fragment key={i}>{i > 0 && <br />}{renderInline(ln, `p-${k}-${i}`, opts)}</React.Fragment>
        ))}
      </p>
    );
  };
  const flushList = (k) => {
    if (!list) return;
    const items = list;
    list = null;
    elements.push(
      <ul key={`ul-${k}`} className="list-disc pl-5 space-y-1">
        {items.map((it, i) => <li key={i}>{renderInline(it, `li-${k}-${i}`, opts)}</li>)}
      </ul>
    );
  };
  lines.forEach((line, k) => {
    if (line.trim() === "") { flushPara(k); flushList(k); return; }
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const heading = line.match(/^\s*#{1,6}\s+(.*)$/);
    if (bullet) { flushPara(k); if (!list) list = []; list.push(bullet[1]); return; }
    if (heading) { flushPara(k); flushList(k); elements.push(<p key={`h-${k}`} className="font-semibold">{renderInline(heading[1], `h-${k}`, opts)}</p>); return; }
    flushList(k); para.push(line);
  });
  flushPara("end"); flushList("end");
  return <div className={`space-y-2 ${className}`}>{elements}</div>;
}

export default MarkdownLite;
